import { useMutation } from "@apollo/client";
import { invert } from "lodash";
import { GoalScope, GoalState } from "types/graphql-schema";

import getArtifactActivitiesQuery from "@apps/artifact-sidebar/graphql/get-artifact-activities-query";
import getDashboardGoalsQuery from "@apps/dashboard-new/graphql/get-dashboard-goals-query";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar, errorNotificationVar } from "@cache/cache";
import { buttonTheme } from "@components/button/button-constants";
import GoalIcon from "@components/goal-icon/goal-icon";
import { useLink } from "@components/link/link-helpers";
import DeprecatedSelect, { SelectOption } from "@components/select/select";
import { classNames, getGoalTextColorClassName } from "@helpers/css";

import updateArtifactMutation from "../graphql/update-artifact-mutation";

export const GoalScopeDropdownInput = ({
  artifact,
  onChangeScope,
}: {
  artifact: {
    scope: GoalScope;
    state: GoalState;
    canUpdate: { permission: boolean };
  };
  onChangeScope: (scope: SelectOption<GoalScope>) => void;
}) => {
  const currentUser = currentUserVar();
  const label = useLabel();
  return (
    <DeprecatedSelect<GoalScope>
      disabled={!artifact.canUpdate.permission}
      buttonProps={{
        theme: buttonTheme.text,
        "aria-label": "Goal scope dropdown button",
      }}
      options={[
        {
          value: GoalScope.Personal,
          label: ({ hovered }) => (
            <span className="flex items-center gap-1">
              <GoalIcon
                state={GoalState.Open}
                scope={GoalScope.Personal}
                size={4}
              />
              <span
                className={classNames(
                  getGoalTextColorClassName(GoalScope.Personal),
                  hovered && "text-white",
                )}
              >
                {label("goal", { titleCase: true })}
              </span>
            </span>
          ),
        },
        {
          value: GoalScope.Team,
          label: ({ hovered }) => (
            <span className="flex items-center gap-1">
              <GoalIcon
                state={GoalState.Open}
                scope={GoalScope.Team}
                size={4}
              />
              <span
                className={classNames(
                  getGoalTextColorClassName(GoalScope.Team),
                  hovered && "text-white",
                )}
              >
                {label("team", { titleCase: true })} {label("goal")}
              </span>
            </span>
          ),
          description: !currentUser.paidFeatures.canCreateGroupGoals
            ? "Upgrade to a higher tier"
            : "",
        },
        {
          value: GoalScope.Organization,
          label: ({ hovered }) => (
            <span className="flex items-center gap-1">
              <GoalIcon
                state={GoalState.Open}
                scope={GoalScope.Organization}
                size={4}
              />
              <span
                className={classNames(
                  getGoalTextColorClassName(GoalScope.Organization),
                  hovered && "text-white",
                )}
              >
                {label("organization", { titleCase: true })} {label("goal")}
              </span>
            </span>
          ),
          description: !currentUser.paidFeatures.canCreateGroupGoals
            ? "Upgrade to a higher tier"
            : "",
        },
        {
          value: GoalScope.Development,
          label: ({ hovered }) => (
            <span className="flex items-center gap-1">
              <GoalIcon
                state={GoalState.Open}
                scope={GoalScope.Development}
                size={4}
              />
              <span
                className={classNames(
                  getGoalTextColorClassName(GoalScope.Development),
                  hovered && "text-white",
                )}
              >
                {label("development", { capitalize: true })} {label("goal")}
              </span>
            </span>
          ),
          description: !currentUser.paidFeatures.canCreateGroupGoals
            ? "Upgrade to a higher tier"
            : "",
        },
      ]}
      value={artifact.scope}
      onChange={onChangeScope}
    />
  );
};

const goalScopeHierarchy = {
  [GoalScope.Personal]: 0,
  [GoalScope.Team]: 1,
  [GoalScope.Organization]: 2,
  [GoalScope.Development]: 0,
} as Record<GoalScope, number>;

const reversedGoalScopeHierarchy = invert(goalScopeHierarchy) as Record<
  number,
  GoalScope
>;

const getScopeLabel = (
  scope: GoalScope,
  label: (key: string, opts?: any) => string,
): string => {
  switch (scope) {
    case GoalScope.Personal:
      return label("goal", { titleCase: true });
    case GoalScope.Team:
      return `${label("team", { titleCase: true })} ${label("goal")}`;
    case GoalScope.Organization:
      return `${label("organization", { titleCase: true })} ${label("goal")}`;
    case GoalScope.Development:
      return `${label("development", { capitalize: true })} ${label("goal")}`;
    default:
      return label("goal", { titleCase: true });
  }
};

const GoalScopeDropdown = ({
  artifact,
}: {
  artifact: {
    id: number;
    scope: GoalScope;
    state: GoalState;
    canUpdate: { permission: boolean };
    parentGoalId?: number | null;
    parentGoal?: { scope: GoalScope } | null;
    basicChildGoals?: {
      edges: ReadonlyArray<{
        node?: {
          scope: GoalScope;
        } | null;
      } | null>;
    } | null;
  };
}) => {
  const link = useLink();
  const label = useLabel();
  const currentUser = currentUserVar();
  const [updateArtifact] = useMutation(updateArtifactMutation);

  const childGoalsCount = artifact.basicChildGoals?.edges.length ?? 0;

  const handleChangeScope = (option: SelectOption<GoalScope>) => {
    if (
      !currentUser.paidFeatures.canCreateGroupGoals &&
      option.value !== GoalScope.Personal
    ) {
      return link.redirect("/billing");
    }

    // Validate parent goal scope hierarchy
    if (
      artifact.parentGoal &&
      goalScopeHierarchy[option.value] >
        goalScopeHierarchy[artifact.parentGoal.scope]
    ) {
      const newScopeLabel = getScopeLabel(option.value, label);
      const parentScopeLabel = getScopeLabel(artifact.parentGoal.scope, label);
      errorNotificationVar({
        title: "Cannot change scope",
        description: `${newScopeLabel} cannot be a child of ${parentScopeLabel}. Please remove the parent ${label("goal")} first or change it to a compatible scope.`,
      });
      return;
    }

    // Validate that development goals cannot have parent goals
    if (
      option.value === GoalScope.Development &&
      (artifact.parentGoalId || childGoalsCount > 0)
    ) {
      errorNotificationVar({
        title: "Cannot change to Development",
        description: `Development ${label("goal")}s cannot have parent or sub ${label("goal")}s. Please remove them first before changing the ${label("goal")} type.`,
      });
      return;
    }

    // Validate child goals scope hierarchy
    if (artifact.basicChildGoals?.edges.length) {
      const childScopes = artifact.basicChildGoals.edges
        .filter((edge) => edge?.node?.scope != null)
        .map((edge) => goalScopeHierarchy[edge!.node!.scope]);

      if (childScopes.length > 0) {
        const maxChildScopeNum = Math.max(...childScopes);
        const optionScopeNum = Number(goalScopeHierarchy[option.value]);

        if (optionScopeNum < maxChildScopeNum) {
          const newScopeLabel = getScopeLabel(option.value, label);
          const value = reversedGoalScopeHierarchy[maxChildScopeNum];
          const childScopeLabel = getScopeLabel(value, label);
          errorNotificationVar({
            title: "Cannot change scope",
            description: `${newScopeLabel} cannot have ${childScopeLabel} as children. Please remove or change the child ${label("goal")}s first.`,
          });
          return;
        }
      }
    }

    updateArtifact({
      variables: {
        artifactId: artifact.id,
        additionalFields: { goalScope: option.value },
      },
      optimisticResponse: {
        createOrUpdateArtifact: {
          artifact: {
            ...artifact,
            scope: option.value,
          },
          __typename: "CreateOrUpdateArtifactMutation",
        },
      },
      refetchQueries: [getDashboardGoalsQuery, getArtifactActivitiesQuery],
    });
  };

  return (
    <GoalScopeDropdownInput
      artifact={artifact}
      onChangeScope={handleChangeScope}
    />
  );
};

export default GoalScopeDropdown;
